// @ts-check
/**
 * Project events recorded in the same window as a change, with the standing note that a coincidence in
 * time is not a cause. The note is fixed UI copy, not model output, so it cannot be argued away.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { TestableText } from '@components/general/TestableComponents';
import { projectTitle } from '@datahooks/ProjectsListContext';
import { renderReportMarkdown } from './AIReportMarkdown.jsx';
import { LuInfo } from 'react-icons/lu';

/** @typedef {import('@datahooks/types/organisationAiReport.types.js').AiReportRecentProjectEvent} AiReportRecentProjectEvent */
/** @typedef {import('@datahooks/ProjectsListContext').BasicProject} BasicProject */

export const NON_CAUSATION_NOTE = 'The AI report does not claim that the event caused the failures.';

/**
 * @param {{ events: AiReportRecentProjectEvent[], projects?: BasicProject[] }} props
 *   projects is metriQ's own list; the report's own name is the fallback.
 */
const AIReportEvents = ({ events = [], projects = [] }) => {
  if (events.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3" data-testid="ai-report-events">
      {events.map((event, index) => (
        <div
          key={`${event.projectId}-${index}`}
          className="rounded-tremor-default border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-dark-tremor-background"
          data-testid={`ai-report-event-${index}`}
        >
          {projectTitle(projects, event.projectId, event.projectName) && (
            <TestableText
              variant="small"
              className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
              dataTestId={`ai-report-event-${index}-project`}
            >
              {projectTitle(projects, event.projectId, event.projectName)}
            </TestableText>
          )}
          <TestableText variant="small" className="text-sm font-semibold text-gray-900 dark:text-gray-100" dataTestId={`ai-report-event-${index}-headline`}>
            {event.headline}
          </TestableText>
          <div
            className="mt-1 space-y-2 text-sm leading-6 text-gray-600 dark:text-gray-400"
            data-testid={`ai-report-event-${index}-detail`}
          >
            {renderReportMarkdown(event.detail, `ai-report-event-${index}-detail`)}
          </div>
        </div>
      ))}

      <TestableText
        variant="small"
        className="flex items-start gap-2 rounded-tremor-small bg-tremor-background-subtle px-2 py-1.5 text-xs leading-5 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
        dataTestId="ai-report-events-note"
      >
        <LuInfo aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {NON_CAUSATION_NOTE}
      </TestableText>
    </div>
  );
};

AIReportEvents.propTypes = {
  events: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.number,
    projectName: PropTypes.string,
    headline: PropTypes.string.isRequired,
    detail: PropTypes.string,
  })),
  projects: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.number,
    title: PropTypes.string,
  })),
};

export default AIReportEvents;
